/**
 * Display UOM conversion for Togal classification quantities.
 * Internal values follow canonical imperial bases (area → SF, linear → FT, volume → CF),
 * then scale to the classification display UOM.
 */

import type { TogalClassification, TogalUnit, TogalViewFeature } from "./types";

export type MeasureFamily = "area" | "linear" | "volume" | "count";

export const QUANTITY_CUSTOM = "custom";
export const COMBINED_VIEW_PREFIX = "Combined";
const DEFAULT_VIEW_MULTIPLIER = 1;

/** Non-null, non-blank trimmed `data.classificationCustomId`, else null. */
export function classificationCustomOrderId(data: TogalClassification["data"] | undefined): string | null {
  const raw = data?.classificationCustomId;
  if (raw == null) return null;
  const t = String(raw).trim();
  return t.length > 0 ? t : null;
}

const UOM_MAP: Record<string, string> = {
  square_inches: "SQIN",
  square_feets: "SF",
  square_yards: "SY",
  square_100sqft: "SQ",
  square_meter: "M2",
  square_centimeter: "CM2",
  square_millimeter: "MM2",
  inches: "IN",
  feet: "FT",
  yard: "YD",
  centimeter: "CM",
  millimeter: "MM",
  meter: "M",
  cubic_feet: "CF",
  cubic_yard: "CY",
  cubic_meter: "M3",
  gallon: "GAL",
  ea: "EA",
  gauge: "GA",
};

/** Map Togal API uom id (e.g. `square_meter`) to short display code (e.g. `M2`). */
export function normalizeUom(uom: string): string {
  return UOM_MAP[uom] ?? uom;
}

export type ResolveFeatureQuantityOptions = {
  unitKey?: string;
  quantityIndex?: number;
  quantityKey?: string;
  classificationType?: string;
  uomId?: string;
};

/**
 * Resolve one feature quantity for a classification unit slot.
 * Custom-formula values are read from `properties.units[i].value` in display UOM (no re-conversion).
 */
export function resolveFeatureQuantity(
  props: Record<string, unknown>,
  unitKeyOrOptions?: string | ResolveFeatureQuantityOptions
): number {
  const options: ResolveFeatureQuantityOptions =
    typeof unitKeyOrOptions === "string" ? { unitKey: unitKeyOrOptions } : (unitKeyOrOptions ?? {});

  const { unitKey, quantityIndex = 0, quantityKey, classificationType, uomId } = options;
  const isCustomQuantity = quantityKey === QUANTITY_CUSTOM;

  if (isCustomQuantity) {
    const units = props.units as TogalUnit[] | undefined;
    const raw = units?.[quantityIndex]?.value;
    const value = typeof raw === "number" && Number.isFinite(raw) ? raw : 0;
    return applyEaCustomCeil(value, uomId);
  }

  const classificationTypeLower = classificationType?.toLowerCase();
  const measureKey = (quantityKey ?? unitKey ?? "").toLowerCase();
  if (classificationTypeLower === "count" && measureKey === "count") {
    return 1;
  }

  if (unitKey) {
    const raw = props[unitKey];
    if (Array.isArray(raw) && typeof raw[0] === "number") return raw[0];
    if (typeof raw === "number") return raw;
  }

  const units = props.units as TogalUnit[] | undefined;
  const fallback = units?.[quantityIndex]?.value;
  return typeof fallback === "number" && Number.isFinite(fallback) ? fallback : 0;
}

/** Custom formula quantities with UOM `ea` are ceiled per feature before summing. */
export function applyEaCustomCeil(value: number, uomId: string | undefined): number {
  if (uomId === "ea" && Number.isFinite(value)) {
    return Math.ceil(value);
  }
  return value;
}

/** Infer measure family from cleaned `units[i].quantity` and classification `data.type`. */
export function inferMeasureFamily(quantityKey: string | undefined, classificationType: string): MeasureFamily {
  const k = (quantityKey ?? "").toLowerCase();
  if (k === "volume") return "volume";
  if (k === "count" || k === "tile_count" || k === "slope_factor") return "count";
  if (k === "length" || k === "perimeter" || k === "length_slope" || k === "total_height" || k === "circumference") {
    return "linear";
  }
  if (
    k === "area" ||
    k === "hor_area" ||
    k === "vertical_area" ||
    k === "area_slope" ||
    k.startsWith("ver_area_") ||
    k === "perimeter_surface_area"
  ) {
    return "area";
  }
  const t = classificationType.toLowerCase();
  if (t === "area") return "area";
  if (t === "line") return "linear";
  if (t === "count") return "count";
  return "linear";
}

const areaConverters: Record<string, (v: number) => number> = {
  square_inches: (v) => v * 144,
  square_feets: (v) => v,
  square_yards: (v) => v / 9,
  square_100sqft: (v) => v / 100,
  square_meter: (v) => v / 10.764,
  square_centimeter: (v) => v * 929.03,
  square_millimeter: (v) => v * 92903,
};

const linearConverters: Record<string, (v: number) => number> = {
  inches: (v) => v * 12,
  feet: (v) => v,
  yard: (v) => v / 3,
  meter: (v) => v / 3.281,
  centimeter: (v) => v * 30.48,
  millimeter: (v) => v * 304.8,
};

const volumeConverters: Record<string, (v: number) => number> = {
  cubic_feet: (v) => v,
  cubic_yard: (v) => v / 27,
  cubic_meter: (v) => v / 35.315,
  gallon: (v) => v * 7.481,
};

/**
 * @param internalValue Sum (or single value) in SF, FT, or CF per `family`, or raw count.
 * @param uomId API / stored classification uom id (e.g. `square_meter`, not display `M2`).
 */
export function convertInternalQuantityToDisplayUom(
  internalValue: number,
  uomId: string,
  family: MeasureFamily
): number {
  const id = uomId.trim();
  if (!id || !Number.isFinite(internalValue)) return internalValue;

  if (family === "count") {
    return internalValue;
  }

  let convert: ((v: number) => number) | undefined;
  if (family === "area") convert = areaConverters[id];
  else if (family === "linear") convert = linearConverters[id];
  else convert = volumeConverters[id];

  return convert ? convert(internalValue) : internalValue;
}

export function pickViewMultiplier(view: {
  id?: string;
  metadata?: { multiplier?: number; combined_multiplier?: number } | null;
}): number {
  const isCombinedView = view.id?.includes(COMBINED_VIEW_PREFIX) ?? false;
  const rawMultiplier = isCombinedView
    ? (view.metadata?.combined_multiplier ?? view.metadata?.multiplier)
    : view.metadata?.multiplier;

  return rawMultiplier != null && !Number.isNaN(rawMultiplier) ? rawMultiplier : DEFAULT_VIEW_MULTIPLIER;
}

/**
 * `view.state` is deliberately not consulted. It carries vectorization pipeline notification states
 * (`VECTORIZATION_TOGAL_COMPLETE`, `VECTORIZATION_SUCCESS`, ...), not a readiness flag, and the
 * Frontend export panel does not filter on it either. The GeoJSON features are the source of truth.
 */
export function shouldIncludeViewForAggregation(
  view: { comparison_page_id?: string | null; page_id?: string },
  activePageIds: Set<string>
): boolean {
  if (view.comparison_page_id != null) return false;
  if (view.page_id && activePageIds.size > 0 && !activePageIds.has(view.page_id)) return false;
  return true;
}

export function featureHasUsableGeometry(feature: TogalViewFeature): boolean {
  const coordinates = feature.geometry as { coordinates?: unknown } | null | undefined;
  if (!coordinates?.coordinates) return false;
  if (Array.isArray(coordinates.coordinates) && coordinates.coordinates.length === 0) return false;
  return true;
}

export type ClassificationQuantitySlot = {
  index: number;
  quantityKey: string;
  uomId: string;
  uom: string;
  unitKey?: string;
  isCustom: boolean;
};

/** Build per-slot metadata from classification `data.units` / `aunits`. */
export function buildClassificationQuantitySlots(cls: TogalClassification): ClassificationQuantitySlot[] {
  const units = cls.data?.units ?? [];
  const aunits = cls.aunits ?? [];
  const slots: ClassificationQuantitySlot[] = [];

  for (let index = 0; index < units.length; index++) {
    const unit = units[index];
    const quantityKey = unit?.quantity;
    if (!quantityKey) continue;

    const unitKey = (aunits[index] as string | undefined) ?? quantityKey;
    const aunit = aunits[index];
    if (aunit && aunit !== quantityKey && !(quantityKey === QUANTITY_CUSTOM && aunit === QUANTITY_CUSTOM)) {
      console.warn(
        `Classification ${cls.id}: aunits[${index}] (${aunit}) does not match units[${index}].quantity (${quantityKey})`
      );
    }

    const uomId = unit.uom ?? "";
    slots.push({
      index,
      quantityKey,
      uomId,
      uom: normalizeUom(uomId),
      unitKey,
      isCustom: quantityKey === QUANTITY_CUSTOM,
    });
  }

  return slots;
}

export const PREDEFINED_GROUP_INCOMPLETE_REASON =
  "Predefined group clones are not included in raw view data; totals may under-count.";

export function classificationMayHaveIncompleteQuantities(
  classification: TogalClassification,
  hasPredefinedGroupFeatures: boolean
): boolean {
  return Boolean(classification.data?.isPredefinedGroup) || hasPredefinedGroupFeatures;
}
